import { GetChargePointsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-charge-points-paginated.model';
import { SetOrRemoveChargePointsLocationModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/set-or-remove-charge-points-location.model';
import { UpdateChargePointOcppConfigurationModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/update-charge-point-ocpp-configuration.model';
import { GatewayChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/outputs/gateway-charge-point.model';
import { GatewayGetTransactionsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/transactions/models/inputs/gateway-get-transactions-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { CreateChannelModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-channel.model';
import { CreateChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-charge-point.model';
import { CreatePhysicalSocketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/create-physical-socket.model';
import { DeleteChannelModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/delete-channel.model';
import { DeleteChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/delete-charge-point.model';
import { DeletePhysicalSocketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/delete-physical-socket.model';
import { RemoteStartTransactionModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/ocpp/remote-start-transaction.model';
import { OCPPRemoteStopTransactionRequestModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/ocpp/remote-stop-transaction.model';
import { TriggerMessageModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/ocpp/trigger-message.model';
import { SetChargePointsTariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/set-charge-points-tariff.model';
import { UpdateChannelModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/update-channel.model';
import { UpdateChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/update-charge-point.model';
import { UpdatePhysicalSocketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/update-physical-socket.model';
import { ChannelModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/charge-point/channel.model';
import { ConfigurationModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/charge-point/ocpp/configuration.model';
import { PhysicalSocketModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/charge-point/physical-socket.model';
import { TransactionModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/transaction/paginated/transaction.model';
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  CHARGE_POINT_FILTERS_LSK,
  CHARGE_POINT_LIST_OPTIONS_LSK,
  CHARGE_POINT_TRANSACTION_FILTERS_LSK,
} from '@app/config/localstorage-keys.const';
import { useCpmsBoGetChannelQuery } from '@app/core/service/cpms-bo-charge-point.service';
import {
  TRANSACTION_LIST_SORT_FIELD,
  TRANSACTION_LIST_SORT_ORDER,
} from '@app/feature/transaction/transaction-list/transaction-list.config';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  CHARGE_POINT_LIST_DEFAULT_OPTIONS,
  CHARGE_POINT_LIST_FILTER_NAME_LIST,
  CHARGE_POINT_LIST_SORT_FIELD,
  CHARGE_POINT_LIST_SORT_ORDER,
  ChargePointListOptionsModel,
} from './charge-point-list/charge-point-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const ChargePointContext = createContext<GatewayChargePointModel | null>(
  null,
);

export const useChargePointContext = (): GatewayChargePointModel => {
  const context = useContext(ChargePointContext);

  if (!context) {
    throw new Error(
      'useChargePointContext must be used within ChargePointContext.Provider',
    );
  }

  return context;
};

interface ChargePointContextProviderProps {
  data: GatewayChargePointModel;
  children: React.ReactNode;
}

export const ChargePointContextProvider = ({
  data,
  children,
}: ChargePointContextProviderProps): React.JSX.Element => {
  return (
    <ChargePointContext.Provider value={data}>
      {children}
    </ChargePointContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface ChargePointListContextProps {
  getPaginatedQueryParameters: GetChargePointsPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetChargePointsPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  columnOptions: ChargePointListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<ChargePointListOptionsModel>>;
}

export const ChargePointListContext =
  createContext<ChargePointListContextProps | null>(null);

export const useChargePointListContext = (): ChargePointListContextProps => {
  const context = useContext(ChargePointListContext);

  if (!context) {
    throw new Error(
      'useChargePointListContext must be used within ChargePointListContext.Provider',
    );
  }

  return context;
};

interface ChargePointListContextProviderProps {
  children: React.ReactNode;
}

export const ChargePointListContextProvider = ({
  children,
}: ChargePointListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetChargePointsPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        channelOcppStatus: [],
        customerId: [],
        locationId: [],
        networkId: [],
        tariffId: [],
        textToSearch: '',
        // Sortable fields
        sortField: CHARGE_POINT_LIST_SORT_FIELD,
        sortOrder: CHARGE_POINT_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...CHARGE_POINT_LIST_FILTER_NAME_LIST,
      ],
      CHARGE_POINT_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<ChargePointListOptionsModel>(
      CHARGE_POINT_LIST_DEFAULT_OPTIONS,
      CHARGE_POINT_LIST_OPTIONS_LSK,
    );

  const value: ChargePointListContextProps = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      columnOptions,
      setColumnOptions,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    columnOptions,
    setColumnOptions,
  ]);

  return (
    <ChargePointListContext.Provider value={value}>
      {children}
    </ChargePointListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterChargePointListContext =
  createContext<DeepPartialSkipArrayKey<GetChargePointsPaginatedModel> | null>(
    null,
  );

export const useFilterChargePointListContext =
  (): DeepPartialSkipArrayKey<GetChargePointsPaginatedModel> => {
    const context = useContext(FilterChargePointListContext);

    if (!context) {
      throw new Error(
        'useFilterChargePointListContext must be used within FilterChargePointListContext.Provider',
      );
    }

    return context;
  };

interface FilterChargePointListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetChargePointsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterChargePointListContextProvider = ({
  data,
  children,
}: FilterChargePointListContextProviderProps): React.JSX.Element => {
  return (
    <FilterChargePointListContext.Provider value={data}>
      {children}
    </FilterChargePointListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchChargePointListContext =
  createContext<DeepPartialSkipArrayKey<GetChargePointsPaginatedModel> | null>(
    null,
  );

export const useFilterSearchChargePointListContext =
  (): DeepPartialSkipArrayKey<GetChargePointsPaginatedModel> => {
    const context = useContext(FilterSearchChargePointListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchChargePointListContext must be used within FilterSearchChargePointListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchChargePointListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetChargePointsPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchChargePointListContextProvider = ({
  data,
  children,
}: FilterSearchChargePointListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchChargePointListContext.Provider value={data}>
      {children}
    </FilterSearchChargePointListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const CreateChargePointContext =
  createContext<DeepPartialSkipArrayKey<CreateChargePointModel> | null>(null);

export const useCreateChargePointContext =
  (): DeepPartialSkipArrayKey<CreateChargePointModel> => {
    const context = useContext(CreateChargePointContext);

    if (!context) {
      throw new Error(
        'useCreateChargePointContext must be used within CreateChargePointContext.Provider',
      );
    }

    return context;
  };

interface CreateChargePointContextProviderProps {
  data: DeepPartialSkipArrayKey<CreateChargePointModel>;
  children: React.ReactNode;
}

export const CreateChargePointContextProvider = ({
  data,
  children,
}: CreateChargePointContextProviderProps): React.JSX.Element => {
  return (
    <CreateChargePointContext.Provider value={data}>
      {children}
    </CreateChargePointContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UpdateChargePointContext = createContext<Pick<
  UpdateChargePointModel,
  'chargePointId' | 'chargePoint'
> | null>(null);

export const useUpdateChargePointContext = (): Pick<
  UpdateChargePointModel,
  'chargePointId' | 'chargePoint'
> => {
  const context = useContext(UpdateChargePointContext);

  if (!context) {
    throw new Error(
      'useUpdateChargePointContext must be used within UpdateChargePointContext.Provider',
    );
  }

  return context;
};

interface UpdateChargePointContextProviderProps {
  data: Pick<UpdateChargePointModel, 'chargePointId' | 'chargePoint'>;
  children: React.ReactNode;
}

export const UpdateChargePointContextProvider = ({
  data,
  children,
}: UpdateChargePointContextProviderProps): React.JSX.Element => {
  return (
    <UpdateChargePointContext.Provider value={data}>
      {children}
    </UpdateChargePointContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const DeleteChargePointContext = createContext<Pick<
  DeleteChargePointModel,
  'id'
> | null>(null);

export const useDeleteChargePointContext = (): Pick<
  DeleteChargePointModel,
  'id'
> => {
  const context = useContext(DeleteChargePointContext);

  if (!context) {
    throw new Error(
      'useDeleteChargePointContext must be used within DeleteChargePointContext.Provider',
    );
  }

  return context;
};

interface DeleteChargePointContextProviderProps {
  data: Pick<DeleteChargePointModel, 'id'>;
  children: React.ReactNode;
}

export const DeleteChargePointContextProvider = ({
  data,
  children,
}: DeleteChargePointContextProviderProps): React.JSX.Element => {
  return (
    <DeleteChargePointContext.Provider value={data}>
      {children}
    </DeleteChargePointContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UpdateChargePointLocationContext =
  createContext<SetOrRemoveChargePointsLocationModel | null>(null);

export const useUpdateChargePointLocationContext =
  (): SetOrRemoveChargePointsLocationModel => {
    const context = useContext(UpdateChargePointLocationContext);

    if (!context) {
      throw new Error(
        'useUpdateChargePointLocationContext must be used within UpdateChargePointLocationModel.Provider',
      );
    }

    return context;
  };

interface UpdateChargePointLocationContextProviderProps {
  data: SetOrRemoveChargePointsLocationModel;
  children: React.ReactNode;
}

export const UpdateChargePointLocationContextProvider = ({
  data,
  children,
}: UpdateChargePointLocationContextProviderProps): React.JSX.Element => {
  return (
    <UpdateChargePointLocationContext.Provider value={data}>
      {children}
    </UpdateChargePointLocationContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UpdateChargePointTariffContext = createContext<Pick<
  SetChargePointsTariffModel,
  'chargePointIds' | 'tariffId'
> | null>(null);

export const useUpdateChargePointTariffContext = (): Pick<
  SetChargePointsTariffModel,
  'chargePointIds' | 'tariffId'
> => {
  const context = useContext(UpdateChargePointTariffContext);

  if (!context) {
    throw new Error(
      'useUpdateChargePointTariffContext must be used within UpdateChargePointTariffContext.Provider',
    );
  }

  return context;
};

interface UpdateChargePointTariffContextProviderProps {
  data: SetChargePointsTariffModel;
  children: React.ReactNode;
}

export const UpdateChargePointTariffContextProvider = ({
  data,
  children,
}: UpdateChargePointTariffContextProviderProps): React.JSX.Element => {
  return (
    <UpdateChargePointTariffContext.Provider value={data}>
      {children}
    </UpdateChargePointTariffContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const ChargePointOcppConfigurationContext =
  createContext<ConfigurationModel | null>(null);

export const useChargePointOcppConfigurationContext =
  (): ConfigurationModel => {
    const context = useContext(ChargePointOcppConfigurationContext);

    if (!context) {
      throw new Error(
        'useChargePointOcppConfigurationContext must be used within ChargePointOcppConfigurationContext.Provider',
      );
    }

    return context;
  };

interface ChargePointOcppConfigurationContextProviderProps {
  data: ConfigurationModel;
  children: React.ReactNode;
}

export const ChargePointOcppConfigurationContextProvider = ({
  data,
  children,
}: ChargePointOcppConfigurationContextProviderProps): React.JSX.Element => {
  return (
    <ChargePointOcppConfigurationContext.Provider value={data}>
      {children}
    </ChargePointOcppConfigurationContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UpdateChargePointOcppConfigurationContext = createContext<
  (UpdateChargePointOcppConfigurationModel & { chargePointId: string }) | null
>(null);

export const useUpdateChargePointOcppConfigurationContext =
  (): UpdateChargePointOcppConfigurationModel & { chargePointId: string } => {
    const context = useContext(UpdateChargePointOcppConfigurationContext);

    if (!context) {
      throw new Error(
        'useUpdateChargePointOcppConfigurationContext must be used within UpdateChargePointOcppConfigurationContext.Provider',
      );
    }
    return context;
  };

interface UpdateChargePointOcppConfigurationContextProviderProps {
  data: UpdateChargePointOcppConfigurationModel & { chargePointId: string };
  children: React.ReactNode;
}

export const UpdateChargePointOcppConfigurationContextProvider = ({
  data,
  children,
}: UpdateChargePointOcppConfigurationContextProviderProps): React.JSX.Element => {
  return (
    <UpdateChargePointOcppConfigurationContext.Provider value={data}>
      {children}
    </UpdateChargePointOcppConfigurationContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const CreateChargePointOcppTriggerMessageContext =
  createContext<DeepPartialSkipArrayKey<
    Pick<
      TriggerMessageModel,
      'chargePointId' | 'requestedMessage' | 'physicalSocketId'
    >
  > | null>(null);

export const useCreateChargePointOcppTriggerMessageContext =
  (): DeepPartialSkipArrayKey<
    Pick<
      TriggerMessageModel,
      'chargePointId' | 'requestedMessage' | 'physicalSocketId'
    >
  > => {
    const context = useContext(CreateChargePointOcppTriggerMessageContext);

    if (!context) {
      throw new Error(
        'useCreateChargePointOcppTriggerMessageContext must be used within CreateChargePointOcppTriggerMessageContext.Provider',
      );
    }
    return context;
  };

interface CreateChargePointOcppTriggerMessageContextProviderProps {
  data: DeepPartialSkipArrayKey<
    Pick<
      TriggerMessageModel,
      'chargePointId' | 'requestedMessage' | 'physicalSocketId'
    >
  >;
  children: React.ReactNode;
}

export const CreateChargePointOcppTriggerMessageContextProvider = ({
  data,
  children,
}: CreateChargePointOcppTriggerMessageContextProviderProps): React.JSX.Element => {
  return (
    <CreateChargePointOcppTriggerMessageContext.Provider value={data}>
      {children}
    </CreateChargePointOcppTriggerMessageContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface ChannelEditModeContextProps {
  isEditMode: boolean;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
}

export const ChannelEditModeContext =
  createContext<ChannelEditModeContextProps | null>(null);

export const useChannelEditModeContext = (): ChannelEditModeContextProps => {
  const context = useContext(ChannelEditModeContext);

  if (!context) {
    throw new Error(
      'useChannelEditModeContext must be used within ChannelEditModeContext.Provider',
    );
  }

  return context;
};

interface ChannelEditModeContextProviderProps {
  children: React.ReactNode;
}

export const ChannelEditModeContextProvider = ({
  children,
}: ChannelEditModeContextProviderProps): React.JSX.Element => {
  const [isEditMode, setIsEditMode] = useState(false);

  const value = useMemo(
    () => ({ isEditMode, setIsEditMode }),
    [isEditMode, setIsEditMode],
  );

  return (
    <ChannelEditModeContext.Provider value={value}>
      {children}
    </ChannelEditModeContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const ChargePointChannelContext = createContext<ChannelModel | null>(
  null,
);

export const useChargePointChannelContext = (): ChannelModel => {
  const context = useContext(ChargePointChannelContext);

  if (!context) {
    throw new Error(
      'useChargePointChannelContext must be used within ChargePointChannelContext.Provider',
    );
  }

  return context;
};

interface ChargePointChannelContextProviderProps {
  data: ChannelModel;
  children: React.ReactNode;
}

export const ChargePointChannelContextProvider = ({
  data,
  children,
}: ChargePointChannelContextProviderProps): React.JSX.Element => {
  const chargePoint = useChargePointContext();

  const { data: value, isSuccess } = useCpmsBoGetChannelQuery(
    {
      chargePointId: chargePoint.id,
      channelId: data.id,
    },
    {
      pollingInterval: 10 * 1000,
    },
  );

  if (!isSuccess) {
    return <div />;
  }

  return (
    <ChargePointChannelContext.Provider value={value}>
      {children}
    </ChargePointChannelContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const CreateChargePointChannelContext =
  createContext<DeepPartialSkipArrayKey<
    Pick<CreateChannelModel, 'chargePointId' | 'channel'>
  > | null>(null);

export const useCreateChargePointChannelContext = (): DeepPartialSkipArrayKey<
  Pick<CreateChannelModel, 'chargePointId' | 'channel'>
> => {
  const context = useContext(CreateChargePointChannelContext);

  if (!context) {
    throw new Error(
      'useUpdateChargePointChannelContext must be used within UpdateChargePointChannelContext.Provider',
    );
  }

  return context;
};

interface CreateChargePointChannelContextProviderProps {
  data: DeepPartialSkipArrayKey<
    Pick<CreateChannelModel, 'chargePointId' | 'channel'>
  >;
  children: React.ReactNode;
}

export const CreateChargePointChannelContextProvider = ({
  data,
  children,
}: CreateChargePointChannelContextProviderProps): React.JSX.Element => {
  return (
    <CreateChargePointChannelContext.Provider value={data}>
      {children}
    </CreateChargePointChannelContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UpdateChargePointChannelContext = createContext<Pick<
  UpdateChannelModel,
  'chargePointId' | 'channelId' | 'channel'
> | null>(null);

export const useUpdateChargePointChannelContext = (): Pick<
  UpdateChannelModel,
  'chargePointId' | 'channelId' | 'channel'
> => {
  const context = useContext(UpdateChargePointChannelContext);

  if (!context) {
    throw new Error(
      'useUpdateChargePointChannelContext must be used within UpdateChargePointChannelContext.Provider',
    );
  }

  return context;
};

interface UpdateChargePointChannelContextProviderProps {
  data: Pick<UpdateChannelModel, 'chargePointId' | 'channelId' | 'channel'>;
  children: React.ReactNode;
}

export const UpdateChargePointChannelContextProvider = ({
  data,
  children,
}: UpdateChargePointChannelContextProviderProps): React.JSX.Element => {
  return (
    <UpdateChargePointChannelContext.Provider value={data}>
      {children}
    </UpdateChargePointChannelContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const DeleteChargePointChannelContext = createContext<Pick<
  DeleteChannelModel,
  'chargePointId' | 'channelId'
> | null>(null);

export const useDeleteChargePointChannelContext = (): Pick<
  DeleteChannelModel,
  'chargePointId' | 'channelId'
> => {
  const context = useContext(DeleteChargePointChannelContext);

  if (!context) {
    throw new Error(
      'useDeleteChargePointChannelContext must be used within DeleteChargePointChannelContext.Provider',
    );
  }

  return context;
};

interface DeleteChargePointChannelContextProviderProps {
  data: Pick<DeleteChannelModel, 'chargePointId' | 'channelId'>;
  children: React.ReactNode;
}

export const DeleteChargePointChannelContextProvider = ({
  data,
  children,
}: DeleteChargePointChannelContextProviderProps): React.JSX.Element => {
  return (
    <DeleteChargePointChannelContext.Provider value={data}>
      {children}
    </DeleteChargePointChannelContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const ChargePointChannelPhysicalSocketContext =
  createContext<PhysicalSocketModel | null>(null);

export const useChargePointChannelPhysicalSocketContext =
  (): PhysicalSocketModel => {
    const context = useContext(ChargePointChannelPhysicalSocketContext);

    if (!context) {
      throw new Error(
        'useChargePointChannelPhysicalSocketContext must be used within ChargePointChannelPhysicalSocketContext.Provider',
      );
    }

    return context;
  };

interface ChargePointChannelPhysicalSocketContextProviderProps {
  data: PhysicalSocketModel;
  children: React.ReactNode;
}

export const ChargePointChannelPhysicalSocketContextProvider = ({
  data,
  children,
}: ChargePointChannelPhysicalSocketContextProviderProps): React.JSX.Element => {
  return (
    <ChargePointChannelPhysicalSocketContext.Provider value={data}>
      {children}
    </ChargePointChannelPhysicalSocketContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const CreateChargePointChannelPhysicalSocketContext =
  createContext<DeepPartialSkipArrayKey<
    Pick<
      CreatePhysicalSocketModel,
      'chargePointId' | 'channelId' | 'physicalSocket'
    >
  > | null>(null);

export const useCreateChargePointChannelPhysicalSocketContext =
  (): DeepPartialSkipArrayKey<
    Pick<
      CreatePhysicalSocketModel,
      'chargePointId' | 'channelId' | 'physicalSocket'
    >
  > => {
    const context = useContext(CreateChargePointChannelPhysicalSocketContext);

    if (!context) {
      throw new Error(
        'useCreateChargePointChannelPhysicalSocketContext must be used within CreateChargePointChannelPhysicalSocketContext.Provider',
      );
    }

    return context;
  };

interface CreateChargePointChannelPhysicalSocketContextProviderProps {
  data: DeepPartialSkipArrayKey<
    Pick<
      CreatePhysicalSocketModel,
      'chargePointId' | 'channelId' | 'physicalSocket'
    >
  >;
  children: React.ReactNode;
}

export const CreateChargePointChannelPhysicalSocketContextProvider = ({
  data,
  children,
}: CreateChargePointChannelPhysicalSocketContextProviderProps): React.JSX.Element => {
  return (
    <CreateChargePointChannelPhysicalSocketContext.Provider value={data}>
      {children}
    </CreateChargePointChannelPhysicalSocketContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UpdateChargePointChannelPhysicalSocketContext = createContext<Pick<
  UpdatePhysicalSocketModel,
  'chargePointId' | 'channelId' | 'physicalSocketId' | 'physicalSocket'
> | null>(null);

export const useUpdateChargePointChannelPhysicalSocketContext = (): Pick<
  UpdatePhysicalSocketModel,
  'chargePointId' | 'channelId' | 'physicalSocketId' | 'physicalSocket'
> => {
  const context = useContext(UpdateChargePointChannelPhysicalSocketContext);

  if (!context) {
    throw new Error(
      'useUpdateChargePointChannelPhysicalSocketContext must be used within UpdateChargePointChannelPhysicalSocketContext.Provider',
    );
  }

  return context;
};

interface UpdateChargePointChannelPhysicalSocketContextProviderProps {
  data: Pick<
    UpdatePhysicalSocketModel,
    'chargePointId' | 'channelId' | 'physicalSocketId' | 'physicalSocket'
  >;
  children: React.ReactNode;
}

export const UpdateChargePointChannelPhysicalSocketContextProvider = ({
  data,
  children,
}: UpdateChargePointChannelPhysicalSocketContextProviderProps): React.JSX.Element => {
  return (
    <UpdateChargePointChannelPhysicalSocketContext.Provider value={data}>
      {children}
    </UpdateChargePointChannelPhysicalSocketContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const DeleteChargePointChannelPhysicalSocketContext = createContext<Pick<
  DeletePhysicalSocketModel,
  'chargePointId' | 'channelId' | 'physicalSocketId'
> | null>(null);

export const useDeleteChargePointChannelPhysicalSocketContext = (): Pick<
  DeletePhysicalSocketModel,
  'chargePointId' | 'channelId' | 'physicalSocketId'
> => {
  const context = useContext(DeleteChargePointChannelPhysicalSocketContext);

  if (!context) {
    throw new Error(
      'useDeleteChargePointChannelPhysicalSocketContext must be used within DeleteChargePointChannelPhysicalSocketContext.Provider',
    );
  }

  return context;
};

interface DeleteChargePointChannelPhysicalSocketContextProviderProps {
  data: Pick<
    DeletePhysicalSocketModel,
    'chargePointId' | 'channelId' | 'physicalSocketId'
  >;
  children: React.ReactNode;
}

export const DeleteChargePointChannelPhysicalSocketContextProvider = ({
  data,
  children,
}: DeleteChargePointChannelPhysicalSocketContextProviderProps): React.JSX.Element => {
  return (
    <DeleteChargePointChannelPhysicalSocketContext.Provider value={data}>
      {children}
    </DeleteChargePointChannelPhysicalSocketContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const ChargePointChannelTransactionContext =
  createContext<TransactionModel | null>(null);

export const useChargePointChannelTransactionContext = (): TransactionModel => {
  const context = useContext(ChargePointChannelTransactionContext);

  if (!context) {
    throw new Error(
      'useChargePointChannelTransactionContext must be used within ChargePointChannelTransactionContext.Provider',
    );
  }

  return context;
};

interface ChargePointChannelTransactionContextProviderProps {
  data: TransactionModel;
  children: React.ReactNode;
}

export const ChargePointChannelTransactionContextProvider = ({
  data,
  children,
}: ChargePointChannelTransactionContextProviderProps): React.JSX.Element => {
  return (
    <ChargePointChannelTransactionContext.Provider value={data}>
      {children}
    </ChargePointChannelTransactionContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface ChargePointTransactionListContextProps {
  getPaginatedQueryParameters: GatewayGetTransactionsPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GatewayGetTransactionsPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
}

export const ChargePointChannelTransactionListContext =
  createContext<ChargePointTransactionListContextProps | null>(null);

export const useChargePointChannelTransactionListContext =
  (): ChargePointTransactionListContextProps => {
    const context = useContext(ChargePointChannelTransactionListContext);

    if (!context) {
      throw new Error(
        'useChargePointChannelTransactionListContext must be used within ChargePointChannelTransactionListContext.Provider',
      );
    }

    return context;
  };

interface ChargePointChannelTransactionListContextProviderProps {
  data: { chargePoint: GatewayChargePointModel };
  children: React.ReactNode;
}

export const ChargePointChannelTransactionListContextProvider = ({
  data,
  children,
}: ChargePointChannelTransactionListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GatewayGetTransactionsPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields (chargePointId excluded: set from context, not user input)
        chargePointId: [data.chargePoint.id],
        // Sortable fields
        sortField: TRANSACTION_LIST_SORT_FIELD,
        sortOrder: TRANSACTION_LIST_SORT_ORDER,
      },
      ['limit', 'offset', 'sortField', 'sortOrder'],
      CHARGE_POINT_TRANSACTION_FILTERS_LSK,
    );

  const value = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      // setGetPaginatedLimitList,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    // setGetPaginatedLimitList,
  ]);

  return (
    <ChargePointChannelTransactionListContext.Provider value={value}>
      {children}
    </ChargePointChannelTransactionListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const StartChargePointChannelTransactionContext = createContext<Pick<
  RemoteStartTransactionModel,
  'chargePointId' | 'channelId' | 'tokenIdentifier'
> | null>(null);

export const useStartChargePointChannelTransactionContext = (): Pick<
  RemoteStartTransactionModel,
  'chargePointId' | 'channelId' | 'tokenIdentifier'
> => {
  const context = useContext(StartChargePointChannelTransactionContext);

  if (!context) {
    throw new Error(
      'useStartChargePointChannelTransactionContext must be used within StartChargePointChannelTransactionContext.Provider',
    );
  }

  return context;
};

interface StartChargePointChannelTransactionContextProviderProps {
  data: Pick<
    RemoteStartTransactionModel,
    'chargePointId' | 'channelId' | 'tokenIdentifier'
  >;
  children: React.ReactNode;
}

export const StartChargePointChannelTransactionContextProvider = ({
  data,
  children,
}: StartChargePointChannelTransactionContextProviderProps): React.JSX.Element => {
  return (
    <StartChargePointChannelTransactionContext.Provider value={data}>
      {children}
    </StartChargePointChannelTransactionContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const StopChargePointChannelTransactionContext = createContext<Pick<
  OCPPRemoteStopTransactionRequestModel,
  'chargePointId' | 'channelId'
> | null>(null);

export const useStopChargePointChannelTransactionContext = (): Pick<
  OCPPRemoteStopTransactionRequestModel,
  'chargePointId' | 'channelId'
> => {
  const context = useContext(StopChargePointChannelTransactionContext);

  if (!context) {
    throw new Error(
      'useStopChargePointChannelTransactionContext must be used within StopChargePointChannelTransactionContext.Provider',
    );
  }

  return context;
};

interface StopChargePointChannelTransactionContextProviderProps {
  data: Pick<
    OCPPRemoteStopTransactionRequestModel,
    'chargePointId' | 'channelId'
  >;
  children: React.ReactNode;
}

export const StopChargePointChannelTransactionContextProvider = ({
  data,
  children,
}: StopChargePointChannelTransactionContextProviderProps): React.JSX.Element => {
  return (
    <StopChargePointChannelTransactionContext.Provider value={data}>
      {children}
    </StopChargePointChannelTransactionContext.Provider>
  );
};
